import { useState, useEffect } from 'react';
import { GetPatientsBalanceResponse_PatientBalance } from '@weave/schema-gen-ts/dist/schemas/payments/messages/commands/commands.pb';
import { shallow } from 'zustand/shallow';
import { useBulkCollectionsFilterStore } from '../../../../../store/bulk-collections-filter-store';
import { useBulkCollectionsQueryBase, DEFAULT_PAGE_SIZE } from './use-bulk-collections-query-base';

export type UseBulkCollectionsPaginationReturn = {
  data: GetPatientsBalanceResponse_PatientBalance[];
  isLoading: boolean; // True whenever data is being fetched (including pagination/filtering)
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  currentPage: number; // 1-indexed, for display
  pageSize: number;
  orderBy?: string; // Current sort order (e.g., "balance desc, first_name asc")
  goNext: () => void;
  goPrev: () => void;
  setPageSize: (size: number) => void;
  setOrderBy: (orderBy?: string) => void;
};

export const useBulkCollectionsPagination = ({ enabled }: { enabled: boolean }): UseBulkCollectionsPaginationReturn => {
  // Stack: index 0 = first page (no token). Each entry is the pageToken for that page.
  // pageTokenStack[0] is always undefined (first page has no token).
  // pageTokenStack[n] is the nextPageToken returned from page n-1.
  const [pageTokenStack, setPageTokenStack] = useState<(string | undefined)[]>([undefined]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [pageSize, setPageSizeState] = useState(DEFAULT_PAGE_SIZE);
  const [orderBy, setOrderByState] = useState<string | undefined>();

  // Reset to page 1 when filters change
  useEffect(() => {
    const unsubscribe = useBulkCollectionsFilterStore.subscribe(
      (state) => ({ balanceType: state.balanceType, searchValue: state.searchValue }),
      () => {
        setPageTokenStack([undefined]);
        setCurrentPageIndex(0);
      },
      { equalityFn: shallow }
    );
    return unsubscribe;
  }, []);

  const {
    data,
    isFetching,
    nextPageToken: rawNextPageToken,
  } = useBulkCollectionsQueryBase({
    enabled,
    pageToken: pageTokenStack[currentPageIndex],
    pageSize,
    orderBy,
    keepPreviousData: true,
  });

  const nextPageToken = isFetching ? undefined : rawNextPageToken;

  const goNext = () => {
    if (isFetching || !nextPageToken) return;
    setPageTokenStack((prev) => {
      const next = [...prev];
      // Only push if we haven't already stored this token
      if (next.length <= currentPageIndex + 1) {
        next.push(nextPageToken);
      }
      return next;
    });
    setCurrentPageIndex((i) => i + 1);
  };

  const goPrev = () => {
    if (currentPageIndex === 0) return;
    setCurrentPageIndex((i) => i - 1);
  };

  const setPageSize = (size: number) => {
    setPageSizeState(size);
    setPageTokenStack([undefined]);
    setCurrentPageIndex(0);
  };

  const setOrderBy = (newOrderBy?: string) => {
    setOrderByState(newOrderBy);
    setPageTokenStack([undefined]);
    setCurrentPageIndex(0);
  };

  return {
    data,
    isLoading: isFetching,
    hasNextPage: !!nextPageToken,
    hasPreviousPage: currentPageIndex > 0,
    currentPage: currentPageIndex + 1,
    pageSize,
    orderBy,
    goNext,
    goPrev,
    setPageSize,
    setOrderBy,
  };
};
